import { Stack, useTheme } from "@mui/material";
import { HintBox } from "src/components/HintBox";
import { NoReferenceIngredient } from "../NoReferenceIngredient";
import { Ingredient } from "src/api/client-api/ingredients/useGetOneIngredient.ts";
import { IngredientReferenceUnified } from "src/api/client-api/ingredientReferenceUnified/types";
import { Loading } from "src/components/utils/Loading";
import { Post } from "src/api/client-api/post/postSchema.ts";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { useNavigate } from "react-router";
import { OlapScoresWithPrevScores } from "src/api/client-api/olapScores/olapScoresSchema";
import { useIngredientContext } from "../../useIngredientContext";
import { ScoreCards } from "src/components/ScoreCards/ScoreCards";
import { URLHandler } from "src/services/URLHandler";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";

export type HintConfig = {
    title: string;
    content: string;
    shouldShow: boolean;
};

export type SupplierScoreBaseProps = {
    ingredient: Ingredient;
    areScoresRefetching?: boolean;
    scores: OlapScoresWithPrevScores | undefined;
    ingredientReference?: IngredientReferenceUnified;
    isIngredientReferenceReady: boolean;
    hintConfig?: HintConfig;
    post: Post | undefined;
};

/**
 * Bridge component between business logic (Scores) and UI (ScoreCard)
 * Maps score data to display components and handles layout
 */
export const SupplierScoreBase = (props: SupplierScoreBaseProps) => {
    const theme = useTheme();
    const { isSsqVersionReady } = useIngredientContext();
    const { isFeatureActivated } = useApplicationParameters();
    const { company } = useCompanyMetadata();
    const isProductUxV2 = useIsProductUxV2();
    const navigate = useNavigate();

    const {
        ingredient,
        areScoresRefetching = false,
        scores,
        ingredientReference,
        isIngredientReferenceReady,
        hintConfig,
        post,
    } = props;

    if (ingredient === undefined || isIngredientReferenceReady === false || isSsqVersionReady === false)
        return <Loading />;

    const canDrilldown = isFeatureActivated("FS-ingredient-score-details-distribution");

    return (
        <Stack gap={theme.spacing(3)}>
            {hintConfig?.shouldShow && <HintBox title={hintConfig.title} content={hintConfig.content} />}
            {isIngredientReferenceReady && !ingredientReference && <NoReferenceIngredient ingredient={ingredient} />}

            <ScoreCards
                appearance={isProductUxV2 ? "product-v2" : "legacy"}
                scores={scores}
                isLoading={scores === undefined || areScoresRefetching}
                referenceScores={ingredient.jsonAttributes?.["referenceScores"]}
                prevCampaignName={scores?.prevCampaignName}
                showSpecificityRate={post?.isLocked}
                showDetails={canDrilldown}
                showDetailsAction={(scoreId: number) => {
                    navigate(URLHandler.ingredient.score(company.id, ingredient.id, scoreId));
                }}
                sourceMetaPayload={scores?.scoreSourceMeta}
                transportScores={scores?.transportScores}
            />
        </Stack>
    );
};
